import type { EnablePassword } from 'components/DeviceCredentials/EnablePasswordsList';
import { isAfter, startOfDay } from 'date-fns';
import { useMemo } from 'react';
import type { DeviceCredential } from '../../DeviceCredentials/DevicePasswordsList';
import type { ExistingVendor } from '../../VendorsApi/vendorTypes';

export type AuthenticationValidation = {
  warningLong?: string;
  warningShort?: string;
  errorLong?: string;
  errorShort?: string;
  hasError: boolean;
  hasWarning: boolean;
};

/**
 * Checks if a credential is expired
 */
const isCredentialExpired = (
  credential: DeviceCredential | EnablePassword,
): boolean => {
  if (credential.expirationDate?.enabled && credential.expirationDate.value) {
    return isAfter(
      startOfDay(new Date()),
      new Date(credential.expirationDate.value),
    );
  }
  return false;
};

/**
 * Checks if any credential in the list is expired
 */
const hasAnyExpiredCredential = (
  credentials: (DeviceCredential | EnablePassword)[],
): boolean => {
  return credentials.some(isCredentialExpired);
};

/**
 * Checks if any vendor is enabled
 */
const hasAnyEnabledVendor = (vendors: ExistingVendor[]): boolean => {
  return vendors.some((vendor) => vendor.isEnabled);
};

/**
 * Hook that computes cross-tab validation state for Authentication tabs
 */
export const useAuthenticationValidation = (
  deviceCredentials: DeviceCredential[],
  vendors: ExistingVendor[],
): AuthenticationValidation => {
  return useMemo(() => {
    // Rule 1: If one of Device Credentials is expired, display warning in Device Credentials
    const hasWarning = hasAnyExpiredCredential(deviceCredentials);
    const warningLongMessage =
      'Some of the stored credentials have expiration dates in the past.';
    const warningShortMessage = 'Expired credentials';

    // Rule 2: If no credential is valid and no vendor is enabled, display error in Device Credentials and Vendors APIs
    const hasError =
      deviceCredentials.length === 0 && !hasAnyEnabledVendor(vendors);
    const errorLongMessage =
      'There are no active Device credentials or enabled Vendor APIs. At least one is required for discovery.';
    const errorShortMessage = 'No credentials / Vendor APIs';

    return {
      hasWarning,
      hasError,
      warningLong: hasWarning ? warningLongMessage : undefined,
      warningShort: hasWarning ? warningShortMessage : undefined,
      errorLong: hasError ? errorLongMessage : undefined,
      errorShort: hasError ? errorShortMessage : undefined,
    } satisfies AuthenticationValidation;
  }, [deviceCredentials, vendors]);
};
